import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixReviewsV1Review from './reviews-v1-review-reviews.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformSDKVideoV2ToRESTVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A Review object includes all of the details related to the review of an entity.
 * An entity is a specific resource to review, for example, a store product.
 * You can manage existing reviews, create new reviews, and retrieve reviews.
 * Read more about [Adding and Setting Up Wix Reviews](https://support.wix.com/en/article/wix-stores-adding-and-setting-up-wix-reviews).
 */
export interface Review {
  /**
   * Review namespace.
   *
   * Supported values:
   * + Wix Stores: `stores`
   *
   * Currently, only Wix Stores is fully integrated with the Wix Reviews app.
   */
  namespace?: string;
  /**
   * ID of the entity to review. For example, a Wix Stores product ID.
   * @maxLength 36
   */
  entityId?: string;
  /**
   * Review ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /** Review content. */
  content?: ReviewContent;
  /**
   * Date and time the review was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the review was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Author of the review. */
  author?: Author;
  /**
   * Reply to the review.
   * @readonly
   */
  reply?: Reply;
  /**
   * Number of site visitors who found the review helpful.
   * @readonly
   */
  foundHelpful?: number;
  /**
   * Number of site visitors who found the review unhelpful.
   * @readonly
   */
  foundUnhelpful?: number;
  /**
   * Helpfulness score.
   *
   * Calculated by subtracting `foundUnhelpful` from `foundHelpful`.
   * @readonly
   */
  helpfulness?: number;
  /**
   * Moderation status of the review.
   * @readonly
   */
  moderation?: Moderation;
  /**
   * Revision number, which increments by 1 each time the review is updated.
   * To prevent conflicting changes,
   * the current `revision` must be passed when updating the review.
   * Ignored when creating a review.
   * @readonly
   */
  revision?: string | null;
  /**
   * Whether the review has been verified.
   * @readonly
   */
  verified?: boolean;
  /**
   * Origin of a review.
   * @readonly
   */
  origin?: Origin;
  /**
   * Date and time when the review was written.
   * This should match `createdDate`, except for reviews imported from another system.
   */
  reviewDate?: Date | null;
  /**
   * Platform-calculated score of review's relevance to the item.
   * Used to sort reviews by relevance.
   * @readonly
   */
  relevanceScore?: number | null;
  /**
   * Number of times a review has been published.
   * @readonly
   */
  publishCount?: number;
  /**
   * Source of a review.
   * @internal
   */
  source?: Source;
}

/** The review content. */
export interface ReviewContent {
  /**
   * Review title.
   * @maxLength 3000
   */
  title?: string | null;
  /**
   * Review body.
   *
   * Max: 3,000 characters
   * @maxLength 3000
   */
  body?: string | null;
  /**
   * List of media items associated with the review.
   *
   * Max: 10 items
   * @maxSize 10
   */
  media?: Media[];
  /**
   * Rating of the review.
   *
   * Min: `1` <br />
   * Max: `5`
   */
  rating?: number;
}

/** Media item associated with the review. */
export interface Media extends MediaMediaOneOf {
  /** Image media item. */
  image?: string;
  /** Video media item. */
  video?: string;
}

/** @oneof */
export interface MediaMediaOneOf {
  /** Image media item. */
  image?: string;
  /** Video media item. */
  video?: string;
}

export interface VideoResolution {
  /**
   * Video URL.
   * @readonly
   */
  url?: string;
  /**
   * Video height.
   * @readonly
   */
  height?: number;
  /**
   * Video width.
   * @readonly
   */
  width?: number;
  /**
   * Video thumbnail.
   * @readonly
   */
  poster?: string;
  /**
   * Video format for example, mp4, hls.
   * @readonly
   */
  format?: string;
}

/** Review author. */
export interface Author {
  /**
   * Contact ID of the author.
   * @format GUID
   * @immutable
   */
  contactId?: string | null;
  /**
   * Display name of the author.
   * @maxLength 100
   */
  authorName?: string | null;
}

/** Supported types of review author. */
export enum AuthorType {
  UNKNOWN = 'UNKNOWN',
  MEMBER = 'MEMBER',
  VISITOR = 'VISITOR',
}

/** @enumType */
export type AuthorTypeWithLiterals =
  | AuthorType
  | 'UNKNOWN'
  | 'MEMBER'
  | 'VISITOR';

/** Reply to the review. */
export interface Reply {
  /**
   * Reply content.
   * @maxLength 1000
   */
  message?: string;
  /**
   * Date and time the reply was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the reply was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/** Moderation status of the review. */
export interface Moderation {
  moderationStatus?: ModerationModerationStatusWithLiterals;
  /**
   * Indicates whether a moderator manually changed the `moderationStatus` of the review.
   *
   * If the `moderationStatus` changed automatically because the review
   * passed the moderation rules or if moderation is turned off, this field is set to `false`.
   * @readonly
   */
  manuallyChanged?: boolean;
}

/** Supported states of the review. */
export enum ModerationModerationStatus {
  /** Unknown moderation status. */
  UNKNOWN = 'UNKNOWN',
  /** The review is approved and published. */
  APPROVED = 'APPROVED',
  /** The review is pending moderation. Moderation can be applied in the Wix user's [dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Freviews/pending). */
  IN_MODERATION = 'IN_MODERATION',
  /** The review is rejected and not visible on the site. */
  REJECTED = 'REJECTED',
  /** Initial status of the review before the moderation process. */
  SUBMITTED = 'SUBMITTED',
}

/** @enumType */
export type ModerationModerationStatusWithLiterals =
  | ModerationModerationStatus
  | 'UNKNOWN'
  | 'APPROVED'
  | 'IN_MODERATION'
  | 'REJECTED'
  | 'SUBMITTED';

export interface Origin {
  /** Origin type. */
  type?: OriginTypeWithLiterals;
  /**
   * The app ID of the origin for the review.
   * Only available when the `origin.type` is `"APP"`.
   */
  appId?: string | null;
}

/** Supported types of review origin. */
export enum OriginType {
  /** Unknown origin type. */
  UNKNOWN = 'UNKNOWN',
  /** Review was created directly by members or site visitors. */
  ORGANIC = 'ORGANIC',
  /** Review was created through an app. Includes reviews created through an app even if the author is a member or site visitor. */
  APP = 'APP',
}

/** @enumType */
export type OriginTypeWithLiterals = OriginType | 'UNKNOWN' | 'ORGANIC' | 'APP';

export interface Source extends SourceSourceTypeOptionsOneOf {
  /**
   * Only available if `source.type` is `"REVIEW_REQUEST"`.
   * See the [Review Requests API](https://dev.wix.com/docs/rest/crm/community/reviews/review-requests/introduction) for more information.
   */
  reviewRequestOptions?: ReviewRequestOptions;
  /** Type of review source. */
  type?: SourceTypeWithLiterals;
}

/** @oneof */
export interface SourceSourceTypeOptionsOneOf {
  /**
   * Only available if `source.type` is `"REVIEW_REQUEST"`.
   * See the [Review Requests API](https://dev.wix.com/docs/rest/crm/community/reviews/review-requests/introduction) for more information.
   */
  reviewRequestOptions?: ReviewRequestOptions;
}

export enum SourceType {
  /**
   * Review was created from a review request.
   * See the [Review Requests API](https://dev.wix.com/docs/rest/crm/community/reviews/review-requests/introduction) for more information.
   */
  REVIEW_REQUEST = 'REVIEW_REQUEST',
}

/** @enumType */
export type SourceTypeWithLiterals = SourceType | 'REVIEW_REQUEST';

export interface ReviewRequestOptions {
  /** Review request ID. */
  _id?: string | null;
}

/** Triggered when a review's moderation status is set to `APPROVED`. */
export interface ReviewPublished {
  /** Review entity */
  review?: Review;
}

/** Triggered when a review's moderation status is changed or a review is created. */
export interface ModerationStatusChanged {
  /** Review entity. */
  review?: Review;
  /**
   * The previous moderation status of the review.
   * - `APPROVED`: The review is approved and published.
   * - `IN_MODERATION`: The review is pending moderation. Moderation can be applied in the Wix user's [dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Freviews/pending).
   * - `REJECTED`: The review is rejected and not visible in the site.
   * - `SUBMITTED`: Initial status of the review before the moderation process.
   */
  previousModerationStatus?: ModerationModerationStatusWithLiterals;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface GetReviewRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId: string;
  /**
   * Whether to return unpublished reviews.
   *
   * Default: `false`
   */
  returnPrivateReviews?: boolean;
}

export interface GetReviewResponse {
  /** Review. */
  review?: Review;
}

export interface CreateReviewAndContactRequest {
  /**
   * Namespace where the review is created.
   * @maxLength 36
   */
  namespace?: string;
  /**
   * ID of the entity to review.
   * @maxLength 36
   */
  entityId?: string;
  /** Name of the review author. Displayed as part of the review. Required for 3rd-party apps. */
  name?: string | null;
  /**
   * Email address of the review author. Required for 3rd-party apps.
   * @format EMAIL
   */
  email?: string | null;
  /** Review content. */
  content?: ReviewContent;
}

export interface CreateReviewResponse {
  /** Created review. */
  review?: Review;
}

export interface CreateReviewRequest {
  /** Review data. */
  review: Review;
}

export interface BulkCreateReviewRequest {
  /**
   * Reviews to create.
   * @minSize 1
   * @maxSize 100
   */
  reviews: Review[];
  /** Whether to return the created entities. */
  returnEntity?: boolean;
}

export interface BulkCreateReviewResponse {
  /**
   * The created reviews.
   * Only returned if the `returnEntity` field is value is `true`.
   */
  results?: BulkReviewResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkReviewResult {
  /** ItemMetadata */
  itemMetadata?: ItemMetadata;
  /** New Reviews */
  review?: Review;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface UpdateReviewContentRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId?: string;
  /** Review content. */
  content?: ReviewContent;
  /**
   * Fieldmask for update.
   * Supported fields: content.title, content.body, content.media, content.rating
   */
  fieldMask?: string[];
}

export interface UpdateReviewContentResponse {
  /** Updated review data. */
  review?: Review;
}

export interface UpdateReviewRequest {
  /** Review to update. */
  review?: Review;
}

export interface UpdateReviewResponse {
  /** Updated review data. */
  review?: Review;
}

export interface DeleteReviewRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId: string;
}

export interface DeleteReviewResponse {
  /** Review entity. */
  review?: Review;
}

export interface BulkDeleteReviewsRequest {
  /**
   * Filter object.
   *
   */
  filter?: Record<string, any> | null;
}

export interface BulkDeleteReviewsResponse {
  /** Bulk job ID. */
  jobId?: string;
}

export interface QueryReviewsRequest {
  /** Review query. */
  query?: QueryV2;
  /**
   * Whether to return private (unpublished) reviews.
   *
   * Default: `false`
   */
  returnPrivateReviews?: boolean;
}

export interface QueryV2 {
  /** Filter object. */
  filter?: Record<string, any> | null;
  /** Sorting options. */
  sort?: Sorting[];
  /** Cursor paging options. */
  cursorPaging?: CursorPaging;
}

/** Sort options. */
export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /**
   * Sort order.
   *
   * Defaults to `ascending`.
   */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Sort by ascending order. */
  ASC = 'ASC',
  /** Sort by descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * The number of items to load.
   * Cursor token returned in the query response. To be used on the next query request, but not the first query request.
   *
   * Max: `100` <br />
   * Default: `50`
   * @max 100
   */
  limit?: number | null;
  /** Cursor returned in last query response. Shouldn't be provided on first page request. */
  cursor?: string | null;
}

export interface QueryReviewsResponse {
  /** Paging metadata. */
  metadata?: PagingMetadataV2;
  /** List of returned reviews. */
  reviews?: Review[];
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag isn't set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /** Cursor pointing to next result page. */
  next?: string | null;
  /** Cursor pointing to previous result page. */
  prev?: string | null;
}

export interface SetReplyRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId: string;
  /**
   * Response to review author.
   * @maxLength 10000
   */
  message: string;
}

export interface SetReplyResponse {
  /** Updated review. */
  review?: Review;
}

export interface RemoveReplyRequest {
  /**
   * ID of review to remove the reply for.
   * @format GUID
   */
  reviewId: string;
}

export interface RemoveReplyResponse {
  /** Updated review. */
  review?: Review;
}

export interface UpdateModerationStatusRequest {
  /**
   * ID of the review to moderate.
   * @format GUID
   */
  reviewId: string;
  /** Moderation status. */
  status?: UpdateModerationStatusRequestModerationStatusWithLiterals;
}

export enum UpdateModerationStatusRequestModerationStatus {
  /** Unknown moderation status. This value isn't used. */
  UNKNOWN_MODERATION_STATUS = 'UNKNOWN_MODERATION_STATUS',
  /** The review has been approved and published, and site visitors can see it. */
  APPROVED = 'APPROVED',
  /** The review is rejected and site visitors can't see it. */
  REJECTED = 'REJECTED',
}

/** @enumType */
export type UpdateModerationStatusRequestModerationStatusWithLiterals =
  | UpdateModerationStatusRequestModerationStatus
  | 'UNKNOWN_MODERATION_STATUS'
  | 'APPROVED'
  | 'REJECTED';

export interface UpdateModerationStatusResponse {
  /** The review after moderation. */
  review?: Review;
}

export interface BulkUpdateModerationStatusRequest {
  /** Reviews to moderate. */
  filter?: Record<string, any> | null;
  /** Moderation status. */
  status?: ModerationStatusWithLiterals;
}

export enum ModerationStatus {
  /** Unknown moderation status. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** The review has been approved and published, and site visitors can see it. */
  APPROVED = 'APPROVED',
  /** The review is rejected and site visitors can't see it. */
  REJECTED = 'REJECTED',
}

/** @enumType */
export type ModerationStatusWithLiterals =
  | ModerationStatus
  | 'UNKNOWN'
  | 'APPROVED'
  | 'REJECTED';

export interface BulkUpdateModerationStatusResponse {
  /** Job ID. */
  jobId?: string;
}

export interface CountReviewsRequest {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   *
   * See [Query Reviews](https://dev.wix.com/docs/rest/crm/community/reviews/reviews/query-reviews) for a list of supported filters.
   */
  filter?: Record<string, any> | null;
  /**
   * Whether to include unpublished reviews in the count.
   *
   * Default: `false`
   */
  includePrivateReviews?: boolean;
}

export interface CountReviewsResponse {
  /** Number of reviews. */
  count?: number;
}

/** @docsIgnore */
export type CountReviewsApplicationErrors = {
  code?: 'TOO_MANY_TO_COUNT';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ReviewCreatedEnvelope {
  entity: Review;
  metadata: EventMetadata;
}

/**
 * Triggered when a review is created.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Reviews
 * @permissionScopeId SCOPE.DC-REVIEWS.MANAGE-REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @webhook
 * @eventType wix.reviews.v1.review_created
 * @slug created
 */
export declare function onReviewCreated(
  handler: (event: ReviewCreatedEnvelope) => void | Promise<void>
): void;

export interface ReviewDeletedEnvelope {
  entity: Review;
  metadata: EventMetadata;
}

/**
 * Triggered when a review is deleted.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Reviews
 * @permissionScopeId SCOPE.DC-REVIEWS.MANAGE-REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @webhook
 * @eventType wix.reviews.v1.review_deleted
 * @slug deleted
 */
export declare function onReviewDeleted(
  handler: (event: ReviewDeletedEnvelope) => void | Promise<void>
): void;

export interface ReviewModerationStatusChangedEnvelope {
  data: ModerationStatusChanged;
  metadata: EventMetadata;
}

/**
 * Triggered when a review's moderation status is changed or a review is created.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Reviews
 * @permissionScopeId SCOPE.DC-REVIEWS.MANAGE-REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @webhook
 * @eventType wix.reviews.v1.review_moderation_status_changed
 * @slug moderation_status_changed
 */
export declare function onReviewModerationStatusChanged(
  handler: (
    event: ReviewModerationStatusChangedEnvelope
  ) => void | Promise<void>
): void;

export interface ReviewPublishedEnvelope {
  data: ReviewPublished;
  metadata: EventMetadata;
}

/**
 * Triggered when a review's moderation status is set to `"APPROVED"`.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Reviews
 * @permissionScopeId SCOPE.DC-REVIEWS.MANAGE-REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @webhook
 * @eventType wix.reviews.v1.review_review_published
 * @slug review_published
 */
export declare function onReviewPublished(
  handler: (event: ReviewPublishedEnvelope) => void | Promise<void>
): void;

export interface ReviewUpdatedEnvelope {
  entity: Review;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a review is updated.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Reviews
 * @permissionScopeId SCOPE.DC-REVIEWS.MANAGE-REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @webhook
 * @eventType wix.reviews.v1.review_updated
 * @slug updated
 */
export declare function onReviewUpdated(
  handler: (event: ReviewUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a review.
 *
 * By default, unpublished reviews aren't returned.
 * To retrieve an unpublished review, pass `returnPrivateReviews` as `true`.
 * @param reviewId - Review ID.
 * @public
 * @requiredField reviewId
 * @param options - Information about the reviews to retrieve.
 * @permissionId REVIEWS.READ_REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @applicableIdentity APP
 * @returns Review.
 * @fqn com.wixpress.critique.reviews.Reviews.GetReview
 */
export async function getReview(
  reviewId: string,
  options?: GetReviewOptions
): Promise<
  NonNullablePaths<
    Review,
    | `namespace`
    | `entityId`
    | `_id`
    | `content.media`
    | `content.rating`
    | `reply.message`
    | `foundHelpful`
    | `foundUnhelpful`
    | `helpfulness`
    | `moderation.moderationStatus`
    | `moderation.manuallyChanged`
    | `verified`
    | `origin.type`
    | `publishCount`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    reviewId: reviewId,
    returnPrivateReviews: options?.returnPrivateReviews,
  });

  const reqOpts = ambassadorWixReviewsV1Review.getReview(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'review.content.media.video' }],
        },
      ])
    )?.review!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          reviewId: '$[0]',
          returnPrivateReviews: '$[1].returnPrivateReviews',
        },
        singleArgumentUnchanged: false,
      },
      ['reviewId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetReviewOptions {
  /**
   * Whether to return unpublished reviews.
   *
   * Default: `false`
   */
  returnPrivateReviews?: boolean;
}

/**
 * Creates a new review using a contact ID.
 *
 * This method requires a contact ID, see the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction).
 *
 * After a review is created, it may require moderation, see the introduction for the Reviews API for more information about moderation.
 *
 * If the `authorName` field is empty, and the review author:
 * - is a site member, their member name is used.
 * - isn't a site member, the field is set to `null`.
 * @param review - Review data.
 * @public
 * @requiredField review
 * @requiredField review.content
 * @requiredField review.content.rating
 * @requiredField review.entityId
 * @requiredField review.namespace
 * @permissionId REVIEWS.CREATE_REVIEW
 * @applicableIdentity APP
 * @returns Created review.
 * @fqn com.wixpress.critique.reviews.Reviews.CreateReview
 */
export async function createReview(
  review: NonNullablePaths<
    Review,
    `content` | `content.rating` | `entityId` | `namespace`,
    3
  >
): Promise<
  NonNullablePaths<
    Review,
    | `namespace`
    | `entityId`
    | `_id`
    | `content.media`
    | `content.rating`
    | `reply.message`
    | `foundHelpful`
    | `foundUnhelpful`
    | `helpfulness`
    | `moderation.moderationStatus`
    | `moderation.manuallyChanged`
    | `verified`
    | `origin.type`
    | `publishCount`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ review: review }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'review.content.media.image' }],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'review.content.media.video' }],
      },
    ]
  );

  const reqOpts = ambassadorWixReviewsV1Review.createReview(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'review.content.media.video' }],
        },
      ])
    )?.review!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { review: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['review']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates multiple reviews.
 *
 * If the `authorName` field is empty, and the review author:
 * - is a site member, their member name is used.
 * - isn't a site member, the field is set to `null`.
 *
 * To create a single review, call Create Review.
 * @param reviews - Reviews to create.
 * @public
 * @requiredField reviews
 * @requiredField reviews.author
 * @requiredField reviews.author.contactId
 * @requiredField reviews.content
 * @requiredField reviews.entityId
 * @requiredField reviews.namespace
 * @permissionId REVIEWS.REVIEW_BULK_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.BulkCreateReview
 */
export async function bulkCreateReview(
  reviews: NonNullablePaths<
    Review,
    `author` | `author.contactId` | `content` | `entityId` | `namespace`,
    3
  >[],
  options?: BulkCreateReviewOptions
): Promise<
  NonNullablePaths<
    BulkCreateReviewResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.review.namespace`
    | `results.${number}.review.entityId`
    | `results.${number}.review._id`
    | `results.${number}.review.content.rating`
    | `results.${number}.review.reply.message`
    | `results.${number}.review.foundHelpful`
    | `results.${number}.review.foundUnhelpful`
    | `results.${number}.review.helpfulness`
    | `results.${number}.review.moderation.moderationStatus`
    | `results.${number}.review.moderation.manuallyChanged`
    | `results.${number}.review.verified`
    | `results.${number}.review.origin.type`
    | `results.${number}.review.publishCount`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      reviews: reviews,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'reviews.content.media.image' }],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'reviews.content.media.video' }],
      },
    ]
  );

  const reqOpts = ambassadorWixReviewsV1Review.bulkCreateReview(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'results.review.content.media.video' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          reviews: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['reviews', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateReviewOptions {
  /** Whether to return the created entities. */
  returnEntity?: boolean;
}

/**
 * Updates a review.
 *
 * Each time the review is updated, `revision` increments by 1.
 * The existing `revision` must be included when updating the review.
 * This ensures you're working with the latest review information, and it prevents unintended overwrites.
 * @param _id - Review ID.
 * @public
 * @requiredField _id
 * @requiredField review
 * @requiredField review.revision
 * @param options - Options to use when updating a review.
 * @permissionId REVIEWS.UPDATE_REVIEW
 * @applicableIdentity APP
 * @returns Updated review data.
 * @fqn com.wixpress.critique.reviews.Reviews.UpdateReview
 */
export async function updateReview(
  _id: string,
  review: NonNullablePaths<UpdateReview, `revision`, 2>
): Promise<
  NonNullablePaths<
    Review,
    | `namespace`
    | `entityId`
    | `_id`
    | `content.media`
    | `content.rating`
    | `reply.message`
    | `foundHelpful`
    | `foundUnhelpful`
    | `helpfulness`
    | `moderation.moderationStatus`
    | `moderation.manuallyChanged`
    | `verified`
    | `origin.type`
    | `publishCount`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ review: { ...review, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'review.content.media.image' }],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'review.content.media.video' }],
      },
    ]
  );

  const reqOpts = ambassadorWixReviewsV1Review.updateReview(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'review.content.media.video' }],
        },
      ])
    )?.review!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { review: '$[1]' },
        explicitPathsToArguments: { 'review.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'review']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateReview {
  /**
   * Review namespace.
   *
   * Supported values:
   * + Wix Stores: `stores`
   *
   * Currently, only Wix Stores is fully integrated with the Wix Reviews app.
   */
  namespace?: string;
  /**
   * ID of the entity to review. For example, a Wix Stores product ID.
   * @maxLength 36
   */
  entityId?: string;
  /**
   * Review ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /** Review content. */
  content?: ReviewContent;
  /**
   * Date and time the review was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the review was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Author of the review. */
  author?: Author;
  /**
   * Reply to the review.
   * @readonly
   */
  reply?: Reply;
  /**
   * Number of site visitors who found the review helpful.
   * @readonly
   */
  foundHelpful?: number;
  /**
   * Number of site visitors who found the review unhelpful.
   * @readonly
   */
  foundUnhelpful?: number;
  /**
   * Helpfulness score.
   *
   * Calculated by subtracting `foundUnhelpful` from `foundHelpful`.
   * @readonly
   */
  helpfulness?: number;
  /**
   * Moderation status of the review.
   * @readonly
   */
  moderation?: Moderation;
  /**
   * Revision number, which increments by 1 each time the review is updated.
   * To prevent conflicting changes,
   * the current `revision` must be passed when updating the review.
   * Ignored when creating a review.
   * @readonly
   */
  revision?: string | null;
  /**
   * Whether the review has been verified.
   * @readonly
   */
  verified?: boolean;
  /**
   * Origin of a review.
   * @readonly
   */
  origin?: Origin;
  /**
   * Date and time when the review was written.
   * This should match `createdDate`, except for reviews imported from another system.
   */
  reviewDate?: Date | null;
  /**
   * Platform-calculated score of review's relevance to the item.
   * Used to sort reviews by relevance.
   * @readonly
   */
  relevanceScore?: number | null;
  /**
   * Number of times a review has been published.
   * @readonly
   */
  publishCount?: number;
  /**
   * Source of a review.
   * @internal
   */
  source?: Source;
}

/**
 * Deletes a review.
 * @param reviewId - Review ID.
 * @public
 * @requiredField reviewId
 * @permissionId REVIEWS.DELETE_REVIEW
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.DeleteReview
 */
export async function deleteReview(
  reviewId: string
): Promise<
  NonNullablePaths<
    DeleteReviewResponse,
    | `review.namespace`
    | `review.entityId`
    | `review._id`
    | `review.content.media`
    | `review.content.rating`
    | `review.reply.message`
    | `review.foundHelpful`
    | `review.foundUnhelpful`
    | `review.helpfulness`
    | `review.moderation.moderationStatus`
    | `review.moderation.manuallyChanged`
    | `review.verified`
    | `review.origin.type`
    | `review.publishCount`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ reviewId: reviewId });

  const reqOpts = ambassadorWixReviewsV1Review.deleteReview(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'review.content.media.video' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { reviewId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['reviewId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes multiple reviews.
 *
 * All reviews that meet the specified `filter` criteria are deleted.
 *
 * To delete a single review, call Delete Review.
 * @public
 * @param options - Information about the reviews to delete.
 * @permissionId REVIEWS.DELETE_REVIEW
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.BulkDeleteReviews
 */
export async function bulkDeleteReviews(
  options?: BulkDeleteReviewsOptions
): Promise<NonNullablePaths<BulkDeleteReviewsResponse, `jobId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts = ambassadorWixReviewsV1Review.bulkDeleteReviews(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDeleteReviewsOptions {
  /** Filter object. */
  filter?: Record<string, any> | null;
}

/**
 * Creates a query to retrieve a list of reviews.
 *
 *
 * The `queryReviews()` method builds a query to retrieve a list of reviews and returns a `ReviewsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `ReviewsQueryBuilder` methods onto the query.
 *
 * `ReviewsQueryBuilder` methods enable you to sort, filter,
 * and control the results that `queryReviews()` returns.
 *
 * The `queryReviews()` method runs with the following defaults, which you can override:
 *
 * - `ascending("_createdDate")`
 * - `limit(100)`
 *
 * The methods that are chained to `queryReviews()` are applied in the order they're called. For example, if you apply `descending("_createdDate")` and then `descending("content.rating")`, the results are sorted first by the `_createdDate`, and then, if there are multiple results with the same `_createdDate`, the items are sorted by `content.rating`.
 * @public
 * @param options - Information about the reviews to retrieve.
 * @permissionId REVIEWS.READ_REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.QueryReviews
 */
export function queryReviews(
  options?: QueryReviewsOptions
): ReviewsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Review,
    'CURSOR',
    QueryReviewsRequest,
    QueryReviewsResponse
  >({
    func: async (payload: QueryReviewsRequest) => {
      const reqOpts = ambassadorWixReviewsV1Review.queryReviews({
        ...payload,
        ...(options ?? {}),
      });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryReviewsRequest['query']) => {
      const args = [query, options] as [
        QueryReviewsRequest['query'],
        QueryReviewsOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryReviewsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'results.content.media.image' },
              { path: 'reviews.content.media.image' },
            ],
          },
          {
            transformFn: transformRESTVideoV2ToSDKVideoV2,
            paths: [
              { path: 'results.content.media.video' },
              { path: 'reviews.content.media.video' },
            ],
          },
        ])
      );

      return {
        items: transformedData?.reviews,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

export interface QueryReviewsOptions {
  /**
   * Whether to return private (unpublished) reviews.
   *
   * Default: `false`
   */
  returnPrivateReviews?: boolean | undefined;
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ReviewsQueryResult extends QueryCursorResult {
  items: Review[];
  query: ReviewsQueryBuilder;
  next: () => Promise<ReviewsQueryResult>;
  prev: () => Promise<ReviewsQueryResult>;
}

export interface ReviewsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'namespace'
      | 'entityId'
      | '_id'
      | 'content.rating'
      | '_createdDate'
      | 'author.contactId'
      | 'helpfulness'
      | 'moderation.moderationStatus'
      | 'verified'
      | 'origin.type'
      | 'relevanceScore',
    value: any
  ) => ReviewsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | 'namespace'
      | 'entityId'
      | '_id'
      | 'content.rating'
      | '_createdDate'
      | 'author.contactId'
      | 'helpfulness'
      | 'moderation.moderationStatus'
      | 'verified'
      | 'origin.type'
      | 'relevanceScore',
    value: any
  ) => ReviewsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | 'content.rating'
      | '_createdDate'
      | 'helpfulness'
      | 'relevanceScore',
    value: any
  ) => ReviewsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | 'content.rating'
      | '_createdDate'
      | 'helpfulness'
      | 'relevanceScore',
    value: any
  ) => ReviewsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | 'content.rating'
      | '_createdDate'
      | 'helpfulness'
      | 'relevanceScore',
    value: any
  ) => ReviewsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | 'content.rating'
      | '_createdDate'
      | 'helpfulness'
      | 'relevanceScore',
    value: any
  ) => ReviewsQueryBuilder;
  in: (
    propertyName:
      | 'namespace'
      | 'entityId'
      | '_id'
      | 'content.rating'
      | '_createdDate'
      | 'author.contactId'
      | 'helpfulness'
      | 'moderation.moderationStatus'
      | 'relevanceScore',
    value: any
  ) => ReviewsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | 'content.rating'
      | '_createdDate'
      | 'helpfulness'
      | 'verified'
      | 'origin.type'
      | 'relevanceScore'
    >
  ) => ReviewsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | 'content.rating'
      | '_createdDate'
      | 'helpfulness'
      | 'verified'
      | 'origin.type'
      | 'relevanceScore'
    >
  ) => ReviewsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ReviewsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => ReviewsQueryBuilder;
  find: () => Promise<ReviewsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.critique.reviews.Reviews.QueryReviews
 * @requiredField query
 */
export async function typedQueryReviews(
  query: ReviewQuery,
  options?: QueryReviewsOptions
): Promise<
  NonNullablePaths<
    QueryReviewsResponse,
    | `reviews`
    | `reviews.${number}.namespace`
    | `reviews.${number}.entityId`
    | `reviews.${number}._id`
    | `reviews.${number}.content.rating`
    | `reviews.${number}.reply.message`
    | `reviews.${number}.foundHelpful`
    | `reviews.${number}.foundUnhelpful`
    | `reviews.${number}.helpfulness`
    | `reviews.${number}.moderation.moderationStatus`
    | `reviews.${number}.moderation.manuallyChanged`
    | `reviews.${number}.verified`
    | `reviews.${number}.origin.type`
    | `reviews.${number}.publishCount`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts = ambassadorWixReviewsV1Review.queryReviews(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.content.media.image' },
            { path: 'reviews.content.media.image' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [
            { path: 'results.content.media.video' },
            { path: 'reviews.content.media.video' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ReviewQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_id',
        'author.contactId',
        'entityId',
        'moderation.moderationStatus',
        'namespace'
      ];
      operators: ['$eq', '$in', '$ne'];
      sort: 'NONE';
    },
    {
      fields: [
        '_createdDate',
        'content.rating',
        'helpfulness',
        'relevanceScore'
      ];
      operators: ['$eq', '$gt', '$gte', '$in', '$lt', '$lte', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['origin.type', 'verified'];
      operators: ['$eq', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['content.media'];
      operators: ['$isEmpty'];
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Review,
  ReviewQuerySpec
>;
export type ReviewQuery = {
  /** 
  Filter object.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sorting options.  
  */
  sort?: {
    /** 
  Name of the field to sort by.  
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.

  Defaults to `ascending`.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Cursor paging options.  
  */
  cursorPaging?: {
    /** 
  The number of items to load.
  Cursor token returned in the query response. To be used on the next query request, but not the first query request.

  Max: `100` <br />
  Default: `50` 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Cursor returned in last query response. Shouldn't be provided on first page request.  
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
};

export const utils = {
  query: {
    ...createQueryUtils<Review, ReviewQuerySpec, ReviewQuery>(),
  },
};

/**
 * Sets a reply on a review.
 *
 * Only 1 reply can be left on a review. Calling Set Reply when a reply
 * already exists overwrites the existing reply.
 * @param reviewId - Review ID.
 * @param message - Response to review author.
 * @public
 * @requiredField message
 * @requiredField reviewId
 * @permissionId REVIEWS.SET_REPLY
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.SetReply
 */
export async function setReply(
  reviewId: string,
  message: string
): Promise<
  NonNullablePaths<
    SetReplyResponse,
    | `review.namespace`
    | `review.entityId`
    | `review._id`
    | `review.content.media`
    | `review.content.rating`
    | `review.reply.message`
    | `review.foundHelpful`
    | `review.foundUnhelpful`
    | `review.helpfulness`
    | `review.moderation.moderationStatus`
    | `review.moderation.manuallyChanged`
    | `review.verified`
    | `review.origin.type`
    | `review.publishCount`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    reviewId: reviewId,
    message: message,
  });

  const reqOpts = ambassadorWixReviewsV1Review.setReply(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'review.content.media.video' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { reviewId: '$[0]', message: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['reviewId', 'message']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Removes reply from a review.
 * @param reviewId - ID of review to remove the reply for.
 * @public
 * @requiredField reviewId
 * @permissionId REVIEWS.REMOVE_REPLY
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.RemoveReply
 */
export async function removeReply(
  reviewId: string
): Promise<
  NonNullablePaths<
    RemoveReplyResponse,
    | `review.namespace`
    | `review.entityId`
    | `review._id`
    | `review.content.media`
    | `review.content.rating`
    | `review.reply.message`
    | `review.foundHelpful`
    | `review.foundUnhelpful`
    | `review.helpfulness`
    | `review.moderation.moderationStatus`
    | `review.moderation.manuallyChanged`
    | `review.verified`
    | `review.origin.type`
    | `review.publishCount`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ reviewId: reviewId });

  const reqOpts = ambassadorWixReviewsV1Review.removeReply(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'review.content.media.video' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { reviewId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['reviewId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates the review moderation status.
 * @param reviewId - ID of the review to moderate.
 * @public
 * @requiredField reviewId
 * @permissionId REVIEWS.MODERATE_REVIEWS
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.UpdateModerationStatus
 */
export async function updateModerationStatus(
  reviewId: string,
  options?: UpdateModerationStatusOptions
): Promise<
  NonNullablePaths<
    UpdateModerationStatusResponse,
    | `review.namespace`
    | `review.entityId`
    | `review._id`
    | `review.content.media`
    | `review.content.rating`
    | `review.reply.message`
    | `review.foundHelpful`
    | `review.foundUnhelpful`
    | `review.helpfulness`
    | `review.moderation.moderationStatus`
    | `review.moderation.manuallyChanged`
    | `review.verified`
    | `review.origin.type`
    | `review.publishCount`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    reviewId: reviewId,
    status: options?.status,
  });

  const reqOpts = ambassadorWixReviewsV1Review.updateModerationStatus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'review.content.media.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'review.content.media.video' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { reviewId: '$[0]', status: '$[1].status' },
        singleArgumentUnchanged: false,
      },
      ['reviewId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateModerationStatusOptions {
  /** Moderation status. */
  status?: UpdateModerationStatusRequestModerationStatusWithLiterals;
}

/**
 * Updates moderation status of multiple reviews. All reviews are updated to the same moderation status.
 *
 * To update moderation status of a single review, call Update Moderation Status.
 * @public
 * @permissionId REVIEWS.MODERATE_REVIEWS
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.BulkUpdateModerationStatus
 */
export async function bulkUpdateModerationStatus(
  options?: BulkUpdateModerationStatusOptions
): Promise<NonNullablePaths<BulkUpdateModerationStatusResponse, `jobId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
    status: options?.status,
  });

  const reqOpts =
    ambassadorWixReviewsV1Review.bulkUpdateModerationStatus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0].filter',
          status: '$[0].status',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateModerationStatusOptions {
  /** Reviews to moderate. */
  filter?: Record<string, any> | null;
  /** Moderation status. */
  status?: ModerationStatusWithLiterals;
}

/**
 * Retrieves the number of reviews that match a specified filter.
 *
 * If a filter isn't passed in the request, the method returns the count of all reviews.
 * @public
 * @permissionId REVIEWS.READ_REVIEWS
 * @permissionId REVIEWS.READ_PRIVATE_REVIEWS
 * @applicableIdentity APP
 * @fqn com.wixpress.critique.reviews.Reviews.CountReviews
 */
export async function countReviews(options?: CountReviewsOptions): Promise<
  NonNullablePaths<CountReviewsResponse, `count`, 2> & {
    __applicationErrorsType?: CountReviewsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
    includePrivateReviews: options?.includePrivateReviews,
  });

  const reqOpts = ambassadorWixReviewsV1Review.countReviews(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0].filter',
          includePrivateReviews: '$[0].includePrivateReviews',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountReviewsOptions {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   *
   * See [Query Reviews](https://dev.wix.com/docs/rest/crm/community/reviews/reviews/query-reviews) for a list of supported filters.
   */
  filter?: Record<string, any> | null;
  /**
   * Whether to include unpublished reviews in the count.
   *
   * Default: `false`
   */
  includePrivateReviews?: boolean;
}
